import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import CircularProgress from '@mui/material/CircularProgress';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Paper from '@mui/material/Paper';
import ListIcon from '@mui/icons-material/ViewList';
import CalendarIcon from '@mui/icons-material/CalendarMonth';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import { eventUrl } from '../lib/slugId';
import { PageHeader } from '../components/PageHeader';
import { LogCard } from '../components/LogCard';
import type { Event as EventModel, Paginated } from '../api/types';

type ViewMode = 'list' | 'calendar';

export function Events() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [events, setEvents] = useState<EventModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<ViewMode>('list');

  useEffect(() => {
    apiFetch<Paginated<EventModel>>('/api/v1/events?include=organizer&sort=starts_at&per_page=100')
      .then((res) => setEvents(res.data))
      .finally(() => setLoading(false));
  }, []);

  // Map events into FullCalendar's format
  const calendarEvents = useMemo(
    () =>
      events.map((e) => ({
        id: String(e.id),
        title: e.title,
        start: e.starts_at,
        end: e.ends_at ?? undefined,
        extendedProps: { event: e },
      })),
    [events]
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
      <PageHeader />
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
        >
          <Typography variant="h4">{t('events.title')}</Typography>
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={(_, v) => v && setView(v)}
            size="small"
          >
            <ToggleButton value="list">
              <ListIcon sx={{ mr: 1 }} /> {t('events.list')}
            </ToggleButton>
            <ToggleButton value="calendar">
              <CalendarIcon sx={{ mr: 1 }} /> {t('events.calendar')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {view === 'list' &&
          (events.length === 0 ? (
            <Typography color="text.secondary">{t('events.noEvents')}</Typography>
          ) : (
            <Stack spacing={2}>
              {events.map((evt) => (
                <LogCard
                  key={evt.id}
                  date={evt.starts_at}
                  title={evt.title}
                  chip={evt.type_label}
                  location={`${evt.location_name}${evt.city ? ', ' + evt.city : ''}`}
                  organizer={evt.organizer?.name}
                  photo={evt.photo_urls[0]}
                  to={eventUrl(evt)}
                />
              ))}
            </Stack>
          ))}

        {view === 'calendar' && (
          <Paper
            sx={(theme) => ({
              p: 2,
              // FullCalendar CSS variables — see https://fullcalendar.io/docs/css-customization
              '--fc-border-color': theme.palette.divider,
              '--fc-page-bg-color': theme.palette.background.paper,
              '--fc-neutral-bg-color': theme.palette.action.hover,
              '--fc-today-bg-color':
                theme.palette.mode === 'dark'
                  ? 'rgba(193, 32, 31, 0.12)'
                  : 'rgba(193, 32, 31, 0.06)',
              '--fc-event-bg-color': theme.palette.primary.main,
              '--fc-event-border-color': theme.palette.primary.main,
              '--fc-event-text-color': theme.palette.primary.contrastText,
              '--fc-list-event-hover-bg-color': theme.palette.action.hover,
              '--fc-button-bg-color': theme.palette.background.paper,
              '--fc-button-border-color': theme.palette.divider,
              '--fc-button-text-color': theme.palette.text.primary,
              '--fc-button-hover-bg-color': theme.palette.action.hover,
              '--fc-button-hover-border-color': theme.palette.divider,
              '--fc-button-active-bg-color': theme.palette.primary.main,
              '--fc-button-active-border-color': theme.palette.primary.main,

              '& .fc': {
                fontFamily: theme.typography.fontFamily,
                color: theme.palette.text.primary,
              },
              '& .fc-theme-standard td, & .fc-theme-standard th': {
                borderColor: theme.palette.divider,
              },
              '& .fc-col-header-cell-cushion, & .fc-daygrid-day-number': {
                color: theme.palette.text.primary,
                textDecoration: 'none',
              },
              '& .fc-col-header-cell': {
                padding: theme.spacing(1, 0),
                fontWeight: 600,
              },
              '& .fc-toolbar-title': {
                fontFamily: `${theme.typography.h6.fontFamily} !important`,
                fontSize: `${theme.typography.h6.fontSize} !important`,
                fontWeight: `${theme.typography.h6.fontWeight} !important`,
                color: theme.palette.text.primary,
              },

              '& .fc-button': {
                boxShadow: 'none !important',
                borderRadius: `${theme.shape.borderRadius}px !important`,
                textTransform: 'none !important',
                fontFamily: `${theme.typography.button.fontFamily} !important`,
                fontWeight: `${theme.typography.button.fontWeight} !important`,
                fontSize: `${theme.typography.button.fontSize ?? '0.875rem'} !important`,
                padding: '6px 14px !important',
                transition: 'background-color 150ms, border-color 150ms',
              },
              '& .fc-button:focus, & .fc-button:focus-visible': {
                boxShadow: 'none !important',
                outline: 'none',
              },
              '& .fc-button-primary:not(:disabled).fc-button-active': {
                color: `${theme.palette.primary.contrastText} !important`,
              },
              '& .fc-button-primary:disabled': {
                opacity: 0.5,
              },

              '& .fc-event': {
                cursor: 'pointer',
                borderRadius: '4px',
                padding: '2px 6px',
                fontFamily: theme.typography.caption.fontFamily,
                fontWeight: 500,
                fontSize: theme.typography.caption.fontSize,
                border: 'none',
              },
              '& .fc-daygrid-event-dot': {
                display: 'none',
              },
              '& .fc-daygrid-event': {
                margin: '1px 2px !important',
              },

              '& .fc-day-today': {
                fontWeight: 700,
              },
              '& .fc-daygrid-day.fc-day-today .fc-daygrid-day-number': {
                color: theme.palette.primary.main,
              },
            })}
          >
            <FullCalendar
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,dayGridWeek',
              }}
              events={calendarEvents}
              eventClick={(info) => {
                const evt = info.event.extendedProps.event as EventModel;
                navigate(eventUrl(evt));
              }}
              height="auto"
            />
          </Paper>
        )}
      </Stack>
    </Container>
  );
}
